<script lang="ts">
  import ModalShell from './ModalShell.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import Icon from './Icon.svelte';
  import { reorderCities } from './api';
  import { createListDrag } from './list-drag.svelte';
  import type { TripCityView } from '~/shared/api.js';

  let {
    tripToken,
    cities,
    onDelete,
    onReorder,
    onClose,
  }: {
    tripToken: string;
    cities: TripCityView[];
    /** Reuses TripPage.deleteCityNow — cascades POI re-tagging. */
    onDelete: (city: TripCityView) => Promise<void> | void;
    /** New city order from drag. Parent updates local state, this
     *  modal fires the server PATCH in the background. */
    onReorder: (cities: TripCityView[]) => void;
    onClose: () => void;
  } = $props();

  const drag = createListDrag({
    ignoreSelector: '.action',
    onCommit(fromIdx, toIdx) {
      const next = [...cities];
      const moved = next.splice(fromIdx, 1)[0]!;
      next.splice(toIdx, 0, moved);
      onReorder(next);
      reorderCities(
        tripToken,
        next.map((c) => c.id),
      ).catch((e) => console.error('Failed to reorder cities:', e));
    },
  });

  let confirmingDeleteId: number | null = $state(null);
  let deletingId: number | null = $state(null);

  const confirmingCity = $derived(
    confirmingDeleteId === null
      ? null
      : (cities.find((c) => c.id === confirmingDeleteId) ?? null),
  );

  async function confirmDelete() {
    if (confirmingDeleteId === null) return;
    const target = cities.find((c) => c.id === confirmingDeleteId);
    confirmingDeleteId = null;
    if (!target) return;
    deletingId = target.id;
    try {
      await onDelete(target);
    } finally {
      deletingId = null;
    }
  }
</script>

<ModalShell title="Manage cities" {onClose} maxWidth={460}>
  {#if cities.length === 0}
    <p class="empty">No cities in this trip yet.</p>
  {:else}
    <ul class="list">
      {#each cities as c, idx (c.id)}
        <li
          bind:this={() => drag.getRowEl(idx), (el) => drag.setRowEl(idx, el)}
          class:dragging={drag.isDraggingRow(idx)}
          style={drag.rowStyle(idx)}
          onpointerdown={(e) => drag.onDown(e, idx)}
          onpointermove={drag.onMove}
          onpointerup={drag.onUp}
          onpointercancel={drag.onCancel}
        >
          <span class="grip" aria-hidden="true">
            <Icon name="grip-vertical" size={14} />
          </span>
          <Icon name="map-pin" size={14} />
          <span class="name truncate">{c.name}</span>
          <span class="region">{c.regionCode}</span>
          <button
            type="button"
            class="action action--destructive"
            aria-label="Delete {c.name}"
            title="Delete"
            disabled={deletingId === c.id}
            onclick={() => (confirmingDeleteId = c.id)}
          >
            <Icon name="trash" size={14} />
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</ModalShell>

{#if confirmingCity}
  <ConfirmDialog
    title="Remove city?"
    message={`Remove "${confirmingCity.name}" from this trip? Your places stay — search just won't focus on ${confirmingCity.name} anymore.`}
    confirmLabel="Remove"
    destructive
    onConfirm={confirmDelete}
    onCancel={() => (confirmingDeleteId = null)}
  />
{/if}

<style>
  .empty {
    margin: 0;
    color: var(--color-muted);
    font-size: 14px;
    text-align: center;
    padding: 1rem 0;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-hairline-soft);
    border-radius: var(--radius-md);
    overflow: hidden;
  }
  .list li {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 0.625rem;
    padding: 0.625rem 0.875rem;
    min-height: 48px;
    touch-action: none;
    background: var(--color-canvas);
    transition: box-shadow 120ms ease;
  }
  .list li.dragging {
    box-shadow: rgba(0, 0, 0, 0.15) 0 8px 24px;
    border-radius: var(--radius-sm);
  }
  .grip {
    display: inline-flex;
    color: var(--color-muted);
    cursor: grab;
  }
  .list li.dragging .grip,
  .list li:active .grip { cursor: grabbing; }
  .list li + li { border-top: 1px solid var(--color-hairline-soft); }
  .name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-ink);
  }
  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .region {
    font-size: 11px;
    font-weight: 600;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    cursor: pointer;
    color: var(--color-muted);
  }
  .action:hover {
    background: var(--color-surface-soft);
    color: var(--color-ink);
  }
  .action--destructive:hover { color: var(--color-error); }
  .action:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
</style>
